import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import HikvisionLiveVideo from './dashboard/HikvisionLiveVideo';
import PanicConversation from './PanicConversation';
import { PANIC_STAGE } from '../lib/panicStage';
import { fetchPanicSnapshotBlob } from '../api/panic';
import { playAlarmBeep } from '../lib/alarmBeep';

const PanicOverlay = ({
  incident,
  stage,
  isMine,
  acceptLoading,
  actionLoading,
  onAccept,
  onHangUp,
  onResolve,
  onResume,
  onSetAside,
}) => {
  const { t } = useTranslation();
  const [snapshotUrl, setSnapshotUrl] = useState(null);
  const [resolution, setResolution] = useState('');
  const stopSoundRef = useRef(null);

  const isRinging = stage === PANIC_STAGE.RINGING;
  const isConversation = stage === PANIC_STAGE.CONVERSATION;
  const isResolution = stage === PANIC_STAGE.RESOLUTION;
  const isAway = stage === PANIC_STAGE.AWAY;
  const alarming = isRinging || isAway;

  useEffect(() => {
    setResolution('');
  }, [incident?.id]);

  useEffect(() => {
    if (!incident?.id || !alarming) return undefined;

    let cancelled = false;
    let blobUrl = null;
    fetchPanicSnapshotBlob(incident.id)
      .then((url) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        blobUrl = url;
        setSnapshotUrl(url);
      })
      .catch(() => {
        if (!cancelled) setSnapshotUrl(null);
      });

    stopSoundRef.current = playAlarmBeep();

    return () => {
      cancelled = true;
      if (stopSoundRef.current) {
        stopSoundRef.current();
        stopSoundRef.current = null;
      }
      setSnapshotUrl(null);
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [incident?.id, alarming]);

  if (!incident || !stage) return null;

  const heading = isConversation
    ? t('panic.onCall')
    : isResolution
      ? t('panic.resolutionTitle')
      : isAway
        ? t('panic.noOperatorOnLine')
        : t('panic.emergency');

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm">
      <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-3xl border border-critical/50 bg-surface-raised p-6 shadow-2xl">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-critical">{heading}</p>
            <h2 className="mt-1 text-2xl font-bold text-white">{incident.school || t('panic.unknownSchool')}</h2>
            <p className="text-sm text-muted-300">
              {incident.station || incident.station_name} · {incident.region}
            </p>
          </div>
          {!isConversation && !isResolution && (
            <button
              type="button"
              onClick={onSetAside}
              className="rounded-full p-2 text-muted-400 hover:bg-surface-subtle hover:text-white"
              aria-label={t('common.close')}
            >
              <span className="material-icons">close</span>
            </button>
          )}
        </div>

        {isAway && (
          <div className="mb-4 rounded-2xl border border-amber-400/40 bg-amber-400/10 p-4 text-sm text-amber-100">
            {isMine ? t('panic.reconnectPrompt') : t('panic.operatorAway')}
          </div>
        )}

        {/* Разговор уже несёт картинку станции, отдельный плеер рядом её бы дублировал. */}
        {isConversation ? (
          <PanicConversation incidentId={incident.id} />
        ) : isResolution ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-300">{t('panic.resolutionHint')}</p>
            <textarea
              autoFocus
              value={resolution}
              onChange={(e) => setResolution(e.target.value)}
              placeholder={t('panic.resolutionPlaceholder')}
              rows={4}
              className="w-full rounded-xl border border-surface-border bg-surface-subtle p-3 text-sm text-white"
            />
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {snapshotUrl && (
              <img
                src={snapshotUrl}
                alt={t('panic.snapshot')}
                className="w-full rounded-2xl border border-surface-border object-cover"
              />
            )}
            {incident.device_id && incident.video_enabled !== false && (
              <HikvisionLiveVideo
                deviceId={incident.device_id}
                videoEnabled
                defaultChannel="101"
                className="min-h-[200px] rounded-2xl"
              />
            )}
          </div>
        )}

        <div className="mt-6 flex flex-wrap gap-3">
          {isRinging && (
            <>
              <button
                type="button"
                disabled={acceptLoading}
                onClick={() => onAccept(incident.id)}
                className="rounded-2xl bg-brand-linear px-6 py-3 text-sm font-semibold text-white disabled:opacity-50"
              >
                {acceptLoading ? t('common.loading') : t('panic.accept')}
              </button>
              <button
                type="button"
                onClick={onSetAside}
                className="rounded-2xl border border-surface-border px-6 py-3 text-sm text-muted-200"
              >
                {t('panic.dismissOverlay')}
              </button>
            </>
          )}

          {isConversation && (
            <button
              type="button"
              disabled={actionLoading}
              onClick={onHangUp}
              className="rounded-2xl border border-surface-border px-6 py-3 text-sm font-semibold text-white disabled:opacity-50"
            >
              {t('panic.hangUp')}
            </button>
          )}

          {isResolution && (
            <>
              <button
                type="button"
                disabled={actionLoading || !resolution.trim()}
                onClick={() => onResolve(resolution)}
                className="rounded-2xl bg-brand-linear px-6 py-3 text-sm font-semibold text-white disabled:opacity-50"
              >
                {t('panic.close')}
              </button>
              <button
                type="button"
                disabled={actionLoading}
                onClick={onResume}
                className="rounded-2xl border border-surface-border px-6 py-3 text-sm text-muted-200 disabled:opacity-50"
              >
                {t('panic.backToConversation')}
              </button>
              <button
                type="button"
                onClick={onSetAside}
                className="rounded-2xl px-6 py-3 text-sm text-muted-400 hover:text-white"
              >
                {t('panic.resolveLater')}
              </button>
            </>
          )}

          {isAway && (
            isMine ? (
              <button
                type="button"
                disabled={actionLoading}
                onClick={onResume}
                className="rounded-2xl bg-brand-linear px-6 py-3 text-sm font-semibold text-white disabled:opacity-50"
              >
                {t('panic.reconnect')}
              </button>
            ) : (
              <button
                type="button"
                onClick={onSetAside}
                className="rounded-2xl border border-surface-border px-6 py-3 text-sm text-muted-200"
              >
                {t('panic.dismissOverlay')}
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default PanicOverlay;
